import { useScrollLock } from '@cosuno/cosuno-ui';
import { useNavigationConfirmationEnvironment } from '@cosuno/react-router';
import { useCallback, useContext, useEffect, useId, useMemo, useRef } from 'react';
import type { Exact } from 'ts-essentials';
import { TypedEventTarget } from 'typescript-event-target';

import { ModalsContext } from './modalsContext';

export class ModalEvent extends CustomEvent<{}> {}

export interface ModalEventMap {
  closed: ModalEvent;
}

export type ModalEvents = TypedEventTarget<ModalEventMap>;

export type ModalCloseReason = 'esc-key' | 'close-button';

export interface ModalStateData {
  id: string;
  isOpen: boolean;
  openModal: () => void;
  closeModal: (closeReason?: ModalCloseReason) => void;
  isActive: boolean;
  isClosingDisabled: boolean;
  navigationConfirmationEnvironmentId: string;
  isNavigatingAway: boolean;
  subscribeToForm: (formId: string) => void;
  params: Exact<{}, {}>;
  events: ModalEvents;
  tryNavigatingAway: (onSuccess: () => void) => void;
}

export interface UseModalStateProps {
  isOpen?: boolean;
  isClosingDisabled?: boolean;
  onClose?: (closeReason?: ModalCloseReason) => void;
}

/**
 * Creates state that can be supplied to modals and used to control them.
 *
 * If you need to associate some variables with a modal (e.g. a delete modal
 * needs a user id to delete), then use `useModalStateWithParams`.
 */
export const useModalState = (props: UseModalStateProps = {}): ModalStateData => {
  const { getIsActive, setAsActive, setAsInactive, getIsOpen } = useContext(ModalsContext);
  const id = useId();
  const { tryNavigatingAway, isNavigatingAway, subscribeToForm, environmentId } =
    useNavigationConfirmationEnvironment();
  const isOpen = getIsOpen(id);
  const isOpenProp = props?.isOpen;
  const isActive = getIsActive(id);
  const upToDateSetAsInactive = useRef(setAsInactive);
  const events = useMemo(() => new TypedEventTarget<ModalEventMap>(), []);

  const { onClose } = props;

  upToDateSetAsInactive.current = setAsInactive;

  useScrollLock(isOpen, id);

  const openModal = useCallback(() => {
    setAsActive(id);
  }, [id, setAsActive]);

  const closeModal = useCallback(
    (closeReason?: ModalCloseReason) => {
      tryNavigatingAway(() => setAsInactive(id));

      // zero-timeout allows to properly manage focus inside onClose
      setTimeout(() => {
        onClose?.(closeReason);
        events.dispatchTypedEvent('closed', new ModalEvent('closed'));
      }, 0);
    },
    [tryNavigatingAway, setAsInactive, id, onClose, events],
  );

  useEffect(() => {
    if (isOpenProp === undefined) return;
    if (isOpenProp) {
      setAsActive(id);
    } else {
      setAsInactive(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpenProp]);

  useEffect(
    () => () => {
      upToDateSetAsInactive.current(id);
    },
    [id],
  );

  return useMemo(
    () => ({
      id,
      isOpen,
      openModal,
      closeModal,
      isActive,
      isClosingDisabled: props?.isClosingDisabled || false,
      navigationConfirmationEnvironmentId: environmentId,
      isNavigatingAway,
      subscribeToForm,
      params: {},
      events,
      tryNavigatingAway,
    }),
    [
      closeModal,
      environmentId,
      events,
      id,
      isActive,
      isNavigatingAway,
      isOpen,
      openModal,
      props?.isClosingDisabled,
      subscribeToForm,
      tryNavigatingAway,
    ],
  );
};
